import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  STAFF_DEPARTMENTS,
  STAFF_DEPARTMENT_LABELS,
  STAFF_ROLES,
  type StaffDepartment,
} from '@kortex/shared';
import { api, fieldErrors } from '../lib/api';
import { useAuth } from '../lib/auth';
import { compact, date, money, money0, thisMonth, titleCase, today } from '../lib/format';
import { PageHeader, Tabs } from '../components/Layout';
import {
  ConfirmDialog,
  Drawer,
  Empty,
  ErrorState,
  Kpi,
  Loading,
  Modal,
  Select,
  StatusChip,
  TextInput,
  useToast,
} from '../components/ui';

interface Employee {
  id: string;
  code: string;
  name: string;
  role: string;
  department: string;
  dateOfJoining: string;
  wageType: 'MONTHLY' | 'DAILY';
  monthlyGross: number;
  dailyWage: number;
  pfEnrolled: boolean;
  esiEnrolled: boolean;
  advanceBalance: number;
  isActive: boolean;
  bankName: string | null;
  bankAccount: string | null;
  ifsc: string | null;
}

interface Payslip {
  employeeId: string;
  code: string;
  name: string;
  role: string;
  department: string;
  daysPaid: number;
  daysInMonth: number;
  otHours: number;
  basic: number;
  hra: number;
  allowance: number;
  overtime: number;
  gross: number;
  pf: number;
  esi: number;
  professionalTax: number;
  advanceRecovered: number;
  deductions: number;
  net: number;
}

interface PayrollPreview {
  period: string;
  status: string;
  headcount: number;
  slips: Payslip[];
  totals: {
    gross: number;
    deductions: number;
    net: number;
    pf: number;
    esi: number;
    employerCost: number;
  };
}

interface AttendanceRow {
  employeeId: string;
  code: string;
  name: string;
  role: string;
  department: string;
  status: string | null;
  otHours: number;
}

const STATUSES = ['PRESENT', 'HALF_DAY', 'ABSENT', 'PAID_LEAVE', 'WEEKLY_OFF'] as const;
const SHORT: Record<string, string> = {
  PRESENT: 'P',
  HALF_DAY: 'HD',
  ABSENT: 'A',
  PAID_LEAVE: 'L',
  WEEKLY_OFF: 'WO',
};

export function PeoplePage() {
  const { can } = useAuth();
  const [tab, setTab] = useState<'payroll' | 'attendance' | 'staff'>(
    can('payroll:view') ? 'payroll' : 'attendance',
  );

  return (
    <div className="stack">
      <PageHeader
        title="Staff & Payroll"
        subtitle="Attendance → earnings → statutory deductions → payslips"
      />
      <Tabs
        tabs={[
          ...(can('payroll:view') ? [{ id: 'payroll' as const, label: 'Payroll run' }] : []),
          { id: 'attendance' as const, label: 'Attendance' },
          { id: 'staff' as const, label: 'Employees' },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === 'payroll' ? <PayrollTab /> : tab === 'attendance' ? <AttendanceTab /> : <StaffTab />}
    </div>
  );
}

function PayrollTab() {
  const queryClient = useQueryClient();
  const { fail, push } = useToast();
  const { can } = useAuth();
  const [period, setPeriod] = useState(thisMonth());
  const [confirming, setConfirming] = useState(false);
  const [slip, setSlip] = useState<Payslip | null>(null);

  const preview = useQuery({
    queryKey: ['payroll', period],
    queryFn: () => api.get<PayrollPreview>(`/api/payroll/preview?period=${period}`),
  });

  const finalize = useMutation({
    mutationFn: () => api.post('/api/payroll/finalize', { period }),
    onSuccess: () => {
      push('Payroll finalized — payslips generated', 'ok');
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ['payroll'] });
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
    onError: (error) => {
      setConfirming(false);
      fail(error);
    },
  });

  if (preview.isLoading) return <Loading label="Computing payroll…" />;
  if (preview.error)
    return <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />;

  const data = preview.data!;
  const finalized = data.status === 'FINALIZED';

  return (
    <div className="stack">
      <div className="kpis">
        <Kpi
          label="Gross wages"
          value={compact(data.totals.gross)}
          foot={`${data.headcount} employees · ${period}`}
        />
        <Kpi
          label="Deductions"
          value={compact(data.totals.deductions)}
          foot={`PF ${money0(data.totals.pf)} · ESI ${money0(data.totals.esi)}`}
          tone="amber"
        />
        <Kpi
          label="Net payable"
          value={compact(data.totals.net)}
          foot={finalized ? 'Run finalized' : 'Draft — not yet finalized'}
          tone="blue"
        />
        <Kpi
          label="Employer cost"
          value={compact(data.totals.employerCost)}
          foot="Gross plus matching PF"
        />
      </div>

      <div className="card">
        <div className="card-head">
          <div className="row gap-6">
            <input
              className="input"
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              style={{ width: 160 }}
            />
            {finalized ? <StatusChip status="FINALIZED" /> : null}
          </div>
          {!finalized && can('payroll:run') ? (
            <button className="btn primary" onClick={() => setConfirming(true)}>
              Finalize payroll
            </button>
          ) : null}
        </div>

        {data.slips.length === 0 ? (
          <Empty message="No active employees to pay" />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th className="num">Days</th>
                  <th className="num">Basic</th>
                  <th className="num">HRA</th>
                  <th className="num">Allowance</th>
                  <th className="num">OT</th>
                  <th className="num">Gross</th>
                  <th className="num">PF</th>
                  <th className="num">ESI</th>
                  <th className="num">PT</th>
                  <th className="num">Advance</th>
                  <th className="num">Net pay</th>
                </tr>
              </thead>
              <tbody>
                {data.slips.map((row) => (
                  <tr key={row.employeeId} className="clickable" onClick={() => setSlip(row)}>
                    <td>
                      <div className="b">{row.name}</div>
                      <div className="xs muted mono">
                        {row.code} · {row.role}
                      </div>
                    </td>
                    <td className="dim">{titleCase(row.department)}</td>
                    <td className="num">
                      {row.daysPaid}
                      <span className="muted">/{row.daysInMonth}</span>
                    </td>
                    <td className="num">{money0(row.basic)}</td>
                    <td className="num">{money0(row.hra)}</td>
                    <td className="num">{money0(row.allowance)}</td>
                    <td className={`num ${row.overtime ? '' : 'muted'}`}>
                      {row.overtime ? money0(row.overtime) : '—'}
                    </td>
                    <td className="num b">{money0(row.gross)}</td>
                    <td className="num dim">{row.pf ? money0(row.pf) : '—'}</td>
                    <td className="num dim">{row.esi ? money0(row.esi) : '—'}</td>
                    <td className="num dim">
                      {row.professionalTax ? money0(row.professionalTax) : '—'}
                    </td>
                    <td className="num dim">
                      {row.advanceRecovered ? money0(row.advanceRecovered) : '—'}
                    </td>
                    <td className="num b">{money0(row.net)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={7}>{data.slips.length} employees</td>
                  <td className="num">{money(data.totals.gross)}</td>
                  <td colSpan={4} className="num">
                    − {money(data.totals.deductions)}
                  </td>
                  <td className="num">{money(data.totals.net)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {confirming ? (
        <ConfirmDialog
          title="Finalize payroll"
          message={`Finalize ${period}? ${money(data.totals.net)} across ${data.headcount} employees. Payslips are frozen, salary advances are recovered, and attendance for the period can no longer be changed.`}
          confirmLabel="Finalize"
          busy={finalize.isPending}
          onConfirm={() => finalize.mutate()}
          onCancel={() => setConfirming(false)}
        />
      ) : null}

      {slip ? <PayslipModal slip={slip} period={period} onClose={() => setSlip(null)} /> : null}
    </div>
  );
}

function PayslipModal({
  slip,
  period,
  onClose,
}: {
  slip: Payslip;
  period: string;
  onClose: () => void;
}) {
  const { session } = useAuth();
  return (
    <Modal
      title={`Payslip · ${slip.name} · ${period}`}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Close
          </button>
          <button className="btn primary" onClick={() => window.print()}>
            Print
          </button>
        </>
      }
    >
      <div className="doc">
        <div className="doc-head">
          <div>
            <h2>{session?.property?.name}</h2>
            <div className="small">GSTIN {session?.property?.gstin}</div>
          </div>
          <div className="doc-meta">
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>PAYSLIP</div>
            <div>{period}</div>
            <div>Generated {date(new Date())}</div>
          </div>
        </div>

        <div className="row between small" style={{ marginBottom: 8 }}>
          <div>
            <div>
              <b>{slip.name}</b> ({slip.code})
            </div>
            <div>
              {slip.role} · {titleCase(slip.department)}
            </div>
          </div>
          <div className="right">
            <div>
              Days paid: {slip.daysPaid} / {slip.daysInMonth}
            </div>
            <div>Overtime: {slip.otHours} hrs</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Earnings</th>
              <th className="num">Amount</th>
              <th>Deductions</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Basic</td>
              <td className="num">{money(slip.basic)}</td>
              <td>Provident fund</td>
              <td className="num">{money(slip.pf)}</td>
            </tr>
            <tr>
              <td>House rent allowance</td>
              <td className="num">{money(slip.hra)}</td>
              <td>ESI</td>
              <td className="num">{money(slip.esi)}</td>
            </tr>
            <tr>
              <td>Other allowances</td>
              <td className="num">{money(slip.allowance)}</td>
              <td>Professional tax</td>
              <td className="num">{money(slip.professionalTax)}</td>
            </tr>
            <tr>
              <td>Overtime</td>
              <td className="num">{money(slip.overtime)}</td>
              <td>Advance recovery</td>
              <td className="num">{money(slip.advanceRecovered)}</td>
            </tr>
            <tr>
              <td>
                <b>Gross earnings</b>
              </td>
              <td className="num">
                <b>{money(slip.gross)}</b>
              </td>
              <td>
                <b>Total deductions</b>
              </td>
              <td className="num">
                <b>{money(slip.deductions)}</b>
              </td>
            </tr>
          </tbody>
        </table>

        <table className="tot-table">
          <tbody>
            <tr className="grand">
              <td>Net pay</td>
              <td className="num">{money(slip.net)}</td>
            </tr>
          </tbody>
        </table>

        <div className="doc-foot">
          <div>Computer-generated payslip; no signature required.</div>
          <div className="right">
            PF and ESI at the statutory rates configured for this property.
          </div>
        </div>
      </div>
    </Modal>
  );
}

function AttendanceTab() {
  const queryClient = useQueryClient();
  const { fail, push } = useToast();
  const { can } = useAuth();
  const [day, setDay] = useState(today());
  const [draft, setDraft] = useState<Record<string, { status: string; otHours: number }>>({});

  const attendance = useQuery({
    queryKey: ['attendance', day],
    queryFn: () => api.get<AttendanceRow[]>(`/api/attendance?date=${day}`),
  });

  useEffect(() => {
    if (!attendance.data) return;
    const next: Record<string, { status: string; otHours: number }> = {};
    for (const row of attendance.data) {
      if (row.status) next[row.employeeId] = { status: row.status, otHours: row.otHours };
    }
    setDraft(next);
  }, [attendance.data]);

  const save = useMutation({
    mutationFn: () =>
      api.post('/api/attendance', {
        date: day,
        entries: Object.entries(draft).map(([employeeId, value]) => ({ employeeId, ...value })),
      }),
    onSuccess: () => {
      push('Attendance saved', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['attendance'] });
      void queryClient.invalidateQueries({ queryKey: ['payroll'] });
    },
    onError: fail,
  });

  if (attendance.isLoading) return <Loading />;
  if (attendance.error)
    return <ErrorState error={attendance.error} onRetry={() => void attendance.refetch()} />;

  const rows = attendance.data ?? [];
  const marked = Object.keys(draft).length;

  return (
    <div className="stack">
      <div className="kpis">
        <Kpi label="Marked" value={`${marked}/${rows.length}`} foot={day} />
        <Kpi
          label="Present"
          value={Object.values(draft).filter((d) => d.status === 'PRESENT').length}
          foot="Full day"
        />
        <Kpi
          label="Absent"
          value={Object.values(draft).filter((d) => d.status === 'ABSENT').length}
          foot="Loss of pay"
          tone="red"
        />
        <Kpi
          label="Overtime hours"
          value={Object.values(draft).reduce((sum, d) => sum + d.otHours, 0)}
          foot="Paid at 1.5× hourly basic"
          tone="amber"
        />
      </div>

      <div className="card">
        <div className="card-head">
          <input
            className="input"
            type="date"
            value={day}
            max={today()}
            onChange={(e) => setDay(e.target.value)}
            style={{ width: 160 }}
          />
          {can('hr:manage') ? (
            <div className="row gap-6">
              <button
                className="btn"
                onClick={() =>
                  setDraft(
                    Object.fromEntries(
                      rows.map((row) => [
                        row.employeeId,
                        { status: 'PRESENT', otHours: draft[row.employeeId]?.otHours ?? 0 },
                      ]),
                    ),
                  )
                }
              >
                Mark all present
              </button>
              <button
                className="btn primary"
                disabled={marked === 0 || save.isPending}
                onClick={() => save.mutate()}
              >
                Save attendance
              </button>
            </div>
          ) : null}
        </div>

        <div>
          {rows.map((row) => (
            <div className="att-row" key={row.employeeId}>
              <div>
                <div className="b">{row.name}</div>
                <div className="xs muted">
                  {row.code} · {row.role} · {titleCase(row.department)}
                </div>
              </div>
              <div className="att-btns">
                {STATUSES.map((status) => (
                  <button
                    key={status}
                    data-s={SHORT[status]}
                    className={draft[row.employeeId]?.status === status ? 'on' : ''}
                    disabled={!can('hr:manage')}
                    onClick={() =>
                      setDraft({
                        ...draft,
                        [row.employeeId]: { status, otHours: draft[row.employeeId]?.otHours ?? 0 },
                      })
                    }
                  >
                    {SHORT[status]}
                  </button>
                ))}
              </div>
              <div className="row gap-4">
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={12}
                  step={0.5}
                  style={{ width: 64 }}
                  disabled={!can('hr:manage')}
                  value={draft[row.employeeId]?.otHours ?? 0}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      [row.employeeId]: {
                        status: draft[row.employeeId]?.status ?? 'PRESENT',
                        otHours: Number(e.target.value) || 0,
                      },
                    })
                  }
                />
                <span className="xs muted">OT hrs</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StaffTab() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const employees = useQuery({
    queryKey: ['employees'],
    queryFn: () => api.get<Employee[]>('/api/employees'),
  });

  if (employees.isLoading) return <Loading />;
  if (employees.error)
    return <ErrorState error={employees.error} onRetry={() => void employees.refetch()} />;

  const rows = employees.data ?? [];
  const wageBill = rows
    .filter((row) => row.isActive)
    .reduce(
      (sum, row) => sum + (row.wageType === 'DAILY' ? row.dailyWage * 30 : row.monthlyGross),
      0,
    );

  return (
    <div className="stack">
      <div className="kpis">
        <Kpi
          label="Headcount"
          value={rows.filter((r) => r.isActive).length}
          foot={`${new Set(rows.map((r) => r.department)).size} departments`}
        />
        <Kpi label="Monthly wage bill" value={compact(wageBill)} foot="Gross, before deductions" />
        <Kpi
          label="Advances outstanding"
          value={money0(rows.reduce((sum, r) => sum + r.advanceBalance, 0))}
          foot="Recovered against future payroll"
          tone="amber"
        />
        <Kpi
          label="Daily-wage staff"
          value={rows.filter((r) => r.wageType === 'DAILY').length}
          foot="Paid on days worked"
          tone="blue"
        />
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Employees</h2>
          {can('hr:manage') ? (
            <button className="btn primary" onClick={() => setCreating(true)}>
              + Employee
            </button>
          ) : null}
        </div>
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th>Joined</th>
                <th>Wage type</th>
                <th className="num">Monthly gross</th>
                <th className="num">Advance</th>
                <th>Statutory</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="clickable" onClick={() => setOpenId(row.id)}>
                  <td>
                    <div className="b">{row.name}</div>
                    <div className="xs muted mono">
                      {row.code} · {row.role}
                    </div>
                  </td>
                  <td className="dim">{titleCase(row.department)}</td>
                  <td>{date(row.dateOfJoining)}</td>
                  <td>
                    {row.wageType === 'DAILY' ? `Daily · ${money0(row.dailyWage)}` : 'Monthly'}
                  </td>
                  <td className="num b">
                    {money0(row.wageType === 'DAILY' ? row.dailyWage * 30 : row.monthlyGross)}
                  </td>
                  <td className={`num ${row.advanceBalance ? 'warn' : 'dim'}`}>
                    {row.advanceBalance ? money0(row.advanceBalance) : '—'}
                  </td>
                  <td className="small row gap-4">
                    {row.pfEnrolled ? <span className="chip ok">PF</span> : null}
                    {row.esiEnrolled ? <span className="chip info">ESI</span> : null}
                  </td>
                  <td>
                    <StatusChip status={row.isActive ? 'ACTIVE' : 'INACTIVE'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {openId ? <EmployeeDrawer id={openId} onClose={() => setOpenId(null)} /> : null}
      {creating ? (
        <EmployeeModal
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['employees'] });
          }}
        />
      ) : null}
    </div>
  );
}

function EmployeeDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  interface Detail extends Employee {
    attendance: Array<{ date: string; status: string; otHours: number }>;
    payslips: Array<{
      period: string;
      status: string;
      daysPaid: number;
      gross: number;
      deductions: number;
      net: number;
    }>;
  }
  const employee = useQuery({
    queryKey: ['employee', id],
    queryFn: () => api.get<Detail>(`/api/employees/${id}`),
  });
  const data = employee.data;

  return (
    <Drawer title={data ? `${data.name} · ${data.code}` : 'Employee'} onClose={onClose}>
      {employee.isLoading || !data ? (
        <Loading />
      ) : (
        <div className="stack">
          <div className="card">
            <div className="card-body row between wrap gap-16">
              <div>
                <h2>{data.role}</h2>
                <div className="small muted">
                  {titleCase(data.department)} · joined {date(data.dateOfJoining)}
                </div>
                <div className="small dim" style={{ marginTop: 5 }}>
                  {data.bankName} {data.bankAccount} · {data.ifsc}
                </div>
              </div>
              <div className="right">
                <div className="muted xs">Monthly gross</div>
                <div style={{ fontSize: 23, fontWeight: 750 }}>
                  {money0(data.wageType === 'DAILY' ? data.dailyWage * 30 : data.monthlyGross)}
                </div>
                {data.advanceBalance > 0 ? (
                  <div className="xs warn">Advance outstanding {money0(data.advanceBalance)}</div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h2>Recent attendance</h2>
            </div>
            <div className="card-body row wrap gap-4">
              {data.attendance.length === 0 ? (
                <span className="muted small">Nothing marked yet</span>
              ) : (
                data.attendance.map((entry) => (
                  <span
                    key={entry.date}
                    className={`chip ${entry.status === 'ABSENT' ? 'danger' : entry.status === 'PRESENT' ? 'ok' : ''}`}
                  >
                    {date(entry.date).slice(0, 6)} {SHORT[entry.status]}
                    {entry.otHours ? ` +${entry.otHours}h` : ''}
                  </span>
                ))
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h2>Payslip history</h2>
            </div>
            {data.payslips.length === 0 ? (
              <Empty message="No finalized payslips yet" />
            ) : (
              <div className="table-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th className="num">Days</th>
                      <th className="num">Gross</th>
                      <th className="num">Deductions</th>
                      <th className="num">Net</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.payslips.map((slip) => (
                      <tr key={slip.period}>
                        <td className="b">{slip.period}</td>
                        <td className="num">{slip.daysPaid}</td>
                        <td className="num">{money0(slip.gross)}</td>
                        <td className="num dim">{money0(slip.deductions)}</td>
                        <td className="num b">{money0(slip.net)}</td>
                        <td>
                          <StatusChip status={slip.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}

const OTHER_ROLE = '__other__';

function EmployeeModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { fail, push } = useToast();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [roleMode, setRoleMode] = useState<'list' | 'other'>('list');
  const [form, setForm] = useState({
    name: '',
    role: 'Steward',
    department: 'FNB',
    dateOfJoining: today(),
    wageType: 'MONTHLY',
    monthlyGross: 0,
    dailyWage: 0,
    pfEnrolled: true,
    esiEnrolled: true,
    phone: '',
    bankName: '',
    bankAccount: '',
    ifsc: '',
    advanceBalance: 0,
  });

  const create = useMutation({
    mutationFn: () => api.post('/api/employees', form),
    onSuccess: () => {
      push('Employee added', 'ok');
      onDone();
    },
    onError: (caught) => {
      // Put the reason on the field it belongs to, so "check the highlighted
      // fields" is true rather than a dead end.
      setErrors(fieldErrors(caught));
      fail(caught);
    },
  });

  const set = (patch: Partial<typeof form>) => {
    setForm({ ...form, ...patch });
    setErrors((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(patch)) delete next[key];
      return next;
    });
  };

  const department = form.department as StaffDepartment;
  const roles = STAFF_ROLES[department] ?? [];

  return (
    <Modal
      title="New employee"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!form.name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Add employee
          </button>
        </>
      }
    >
      <div className="form-grid">
        <TextInput
          className="span2"
          label="Full name *"
          autoFocus
          value={form.name}
          error={errors['name']}
          onChange={(e) => set({ name: e.target.value })}
        />

        {/* Department first: it decides which job titles are on offer. */}
        <Select
          label="Department"
          value={form.department}
          error={errors['department']}
          onChange={(e) => {
            const chosen = e.target.value as StaffDepartment;
            // The old role belongs to the old department.
            const first = STAFF_ROLES[chosen]?.[0] ?? '';
            setRoleMode('list');
            set({ department: chosen, role: first });
          }}
        >
          {STAFF_DEPARTMENTS.map((option) => (
            <option key={option} value={option}>
              {STAFF_DEPARTMENT_LABELS[option]}
            </option>
          ))}
        </Select>

        {roleMode === 'list' ? (
          <Select
            label="Role"
            value={roles.includes(form.role) ? form.role : OTHER_ROLE}
            error={errors['role']}
            onChange={(e) => {
              if (e.target.value === OTHER_ROLE) {
                setRoleMode('other');
                set({ role: '' });
              } else {
                set({ role: e.target.value });
              }
            }}
          >
            {roles.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
            <option value={OTHER_ROLE}>Something else…</option>
          </Select>
        ) : (
          <TextInput
            label="Role"
            placeholder="What you call the job"
            value={form.role}
            error={errors['role']}
            hint={
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => {
                  setRoleMode('list');
                  set({ role: roles[0] ?? '' });
                }}
              >
                Pick from the list instead
              </button>
            }
            onChange={(e) => set({ role: e.target.value })}
          />
        )}
        <TextInput
          label="Date of joining"
          type="date"
          value={form.dateOfJoining}
          error={errors['dateOfJoining']}
          onChange={(e) => set({ dateOfJoining: e.target.value })}
        />
        <Select
          label="Wage type"
          value={form.wageType}
          error={errors['wageType']}
          onChange={(e) => set({ wageType: e.target.value })}
        >
          <option value="MONTHLY">Monthly salary</option>
          <option value="DAILY">Daily wage</option>
        </Select>
        <TextInput
          label="Monthly gross (₹)"
          type="number"
          value={form.monthlyGross}
          error={errors['monthlyGross']}
          disabled={form.wageType !== 'MONTHLY'}
          hint={
            form.wageType === 'MONTHLY'
              ? 'What they are paid a month, before deductions'
              : undefined
          }
          onChange={(e) => set({ monthlyGross: Number(e.target.value) || 0 })}
        />
        <TextInput
          label="Daily wage (₹)"
          type="number"
          value={form.dailyWage}
          error={errors['dailyWage']}
          disabled={form.wageType !== 'DAILY'}
          hint={form.wageType === 'DAILY' ? 'Paid for each day worked' : undefined}
          onChange={(e) => set({ dailyWage: Number(e.target.value) || 0 })}
        />
        <TextInput
          label="Phone"
          value={form.phone}
          error={errors['phone']}
          onChange={(e) => set({ phone: e.target.value })}
        />
        <TextInput
          label="Bank"
          value={form.bankName}
          error={errors['bankName']}
          onChange={(e) => set({ bankName: e.target.value })}
        />
        <TextInput
          label="Account number"
          value={form.bankAccount}
          error={errors['bankAccount']}
          onChange={(e) => set({ bankAccount: e.target.value })}
        />
        <TextInput
          label="IFSC"
          placeholder="ICIC0000736"
          hint="4 letters, then a zero, then 6 more"
          value={form.ifsc}
          error={errors['ifsc']}
          onChange={(e) => set({ ifsc: e.target.value.toUpperCase() })}
        />
        <div className="field row gap-16" style={{ flexDirection: 'row', alignItems: 'center' }}>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.pfEnrolled}
              onChange={(e) => set({ pfEnrolled: e.target.checked })}
            />
            PF eligible
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.esiEnrolled}
              onChange={(e) => set({ esiEnrolled: e.target.checked })}
            />
            ESI eligible
          </label>
        </div>
      </div>
    </Modal>
  );
}
